import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, getDocs, addDoc } from "firebase/firestore";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import NewScheduleModal from "../components/NewScheduleModal";
import { useNavigate } from "react-router-dom";
import "./NewSchedule.css";

const NewSchedule = () => {
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [events, setEvents] = useState([]);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [scheduleName, setScheduleName] = useState(""); // Schedule name input
  const [currentView, setCurrentView] = useState("timeGridWeek");

  useEffect(() => {
    const fetchSchedules = async () => {
      const schedulesRef = collection(db, "schedules");
      const querySnapshot = await getDocs(schedulesRef);

      const fetchedSchedules = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        title: doc.data().contentName || "Unnamed Schedule",
        start: new Date(doc.data().startTime),
        end: new Date(doc.data().endTime),
      }));
      setEvents(fetchedSchedules);

      const items = querySnapshot.docs.flatMap((doc) =>
        (doc.data().timeBlocks || []).map((block) => ({
          ...block,
          scheduleId: doc.id,
        }))
      );
      setScheduleItems(items);
    };

    fetchSchedules();
  }, []);

  const handleDateSelect = (selectInfo) => {
    setSelectedTime({
      start: selectInfo.startStr,
      end: new Date(new Date(selectInfo.startStr).getTime() + 60 * 60 * 1000).toISOString(),
    });
    setModalOpen(true);
  };

  const handleViewChange = (viewType) => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.changeView(viewType);
    setCurrentView(viewType);
  };

  const addNewEvent = (newEvent) => {
    setEvents((prevEvents) => [...prevEvents, newEvent]);
  };

  const handleSaveSchedule = async () => {
    if (!scheduleName.trim()) {
      alert("⚠️ Please enter a schedule name.");
      return;
    }

    try {
      const newSchedule = {
        name: scheduleName,
        events: events.map((event) => ({
          title: event.title,
          start: event.start,
          end: event.end,
        })),
        createdAt: new Date(),
      };

      await addDoc(collection(db, "schedules"), newSchedule);
      alert("✅ Schedule saved successfully!");
      navigate("/schedules");
    } catch (error) {
      console.error("❌ Error saving schedule:", error.message);
      alert("⚠️ Failed to save schedule. Please try again.");
    }
  };

  return (
    <div className="new-schedule-container">
      <div className="schedule-header">
        <h1 className="schedule-title">Create New Schedule</h1>
      </div>

      {/* Schedule Name Input */}
      <div className="schedule-name-container">
        <label htmlFor="schedule-name" className="schedule-name-label">Schedule Name:</label>
        <input
          type="text"
          id="schedule-name"
          className="schedule-name-input"
          placeholder="Enter schedule name"
          value={scheduleName}
          onChange={(e) => setScheduleName(e.target.value)}
        />
      </div>

      {/* View Switcher */}
      <div className="schedule-controls">
        <div className="view-switcher">
          <button className={`btn view-mode ${currentView === "dayGridMonth" ? "active" : ""}`} onClick={() => handleViewChange("dayGridMonth")}>
            Monthly View
          </button>
          <button className={`btn view-mode ${currentView === "timeGridWeek" ? "active" : ""}`} onClick={() => handleViewChange("timeGridWeek")}>
            Weekly View
          </button>
          <button className={`btn view-mode ${currentView === "timeGridDay" ? "active" : ""}`} onClick={() => handleViewChange("timeGridDay")}>
            Daily View
          </button>
        </div>
      </div>

      {/* Calendar and Content/Playlist Section */}
      <div className="main-content">
        <div className="calendar-container">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            selectable={true}
            events={events}
            select={handleDateSelect}
          />
        </div>

        <div className="content-playlist-section">
          <h3>Assigned Content & Playlists</h3>
          <ul className="assigned-list">
            {scheduleItems.map((item, index) => (
              <li key={index} onClick={() => setEditingItem(item)}>
                {item.contentName || item.playlistName || "Unnamed Item"} ({item.startTime} - {item.endTime})
              </li>
            ))}
          </ul>
        </div>
      </div>

      {modalOpen && (
        <NewScheduleModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingItem(null);
          }}
          initialStartTime={selectedTime?.start}
          initialEndTime={selectedTime?.end}
          editingItem={editingItem}
          addNewEvent={addNewEvent}
        />
      )}

      {/* Save and Cancel Buttons */}
      <div className="schedule-actions">
        <button className="btn save" onClick={handleSaveSchedule}>
          Save
        </button>
        <button className="btn cancel" onClick={() => navigate("/schedules")}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default NewSchedule;
